import { Col, Row } from "reactstrap";
import React, { useEffect, useMemo, useRef } from "react";
import InputFieldText from "Components/Common/InputFieldText";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";
import SelectField from "Components/Common/SelectField";
import { Box, Checkbox, Typography, useTheme } from "@mui/material";
import { DocumentName, UploadImage } from "./style";
import Tooltip from "Components/Common/Tooltip";
import getFileExtension from "utils/getFileExtension";
import { getImageUrl } from "utils";
import { get } from "lodash";
import { toast } from "react-toastify";
import AddIcon from "@mui/icons-material/Add";
import pdfImg from "assets/images/pdf.png";
import { useLazyGetAllGrievanceCategory } from "apis/CGRFGiftPCL";

const validFileFormats = ["jpg", "png", "jpeg", "pdf"];

const ComplainDetails = ({ formik, t, isDisabled }) => {
  const [
    getGrievanceCategory,
    { data: { data: categoryOptions } = {}, isLoading: isCategoryLoading },
  ] = useLazyGetAllGrievanceCategory();
  const updatedCategoryOptions = useMemo(() => {
    const options =
      categoryOptions?.map(({ id, name }) => ({
        label: name,
        value: id,
      })) ?? [];
    return [...options, { label: "Other", value: 0 }];
  }, [categoryOptions]);
  const {
    errors,
    touched,
    setFieldValue,
    values: { complainDetails: { grievanceCategoryName, documents } = {} },
  } = formik;

  const theme = useTheme();
  const ref = useRef();

  const errorMessage = useMemo(
    () => get(errors, "complainDetails.documents", ""),
    [get(errors, "complainDetails.documents", "")]
  );

  const isError = useMemo(
    () => !!get(touched, "complainDetails.documents", "") && !!errorMessage,
    [get(touched, "complainDetails.documents", ""), errorMessage]
  );

  const fileChangeHandler = (e) => {
    const files = Array.from(e.target.files);
    const isAllFileValid = files.every(
      (file) =>
        validFileFormats.includes(getFileExtension(file.name.toLowerCase())) &&
        file.size < 10485760
    );

    if (!isAllFileValid) {
      toast.error(
        "Please upload image or pdf file and size should be less than 10 mb"
      );
    } else {
      setFieldValue("complainDetails.documents", [
        ...documents,
        ...files.map((file) => ({
          id: 0,
          documentUrl: URL.createObjectURL(file),
          documentName: file.name,
          file: file,
        })),
      ]);
    }
    ref.current.value = "";
  };

  useEffect(() => {
    (async () => {
      try {
        await getGrievanceCategory();
      } catch (err) {}
    })();
  }, []);

  const isOtherCategorySelected = grievanceCategoryName?.find(
    (item) => item.label === "Other"
  );
  return (
    <>
      <Row>
        <Col md={4} sm={6}>
          <SelectField
            label={t("Category of grievance")}
            name="complainDetails.grievanceCategoryName"
            multiple
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => {
              return (
                <li {...props} key={option.value}>
                  <Checkbox sx={{ marginRight: "8px" }} checked={selected} />
                  {option.label}
                </li>
              );
            }}
            formik={formik}
            onChange={(event, options) => {
              if (event.key !== "Backspace" && event.key !== "Delete") {
                setFieldValue("complainDetails.grievanceCategoryName", options);
              }
            }}
            value={grievanceCategoryName}
            options={updatedCategoryOptions}
            placeholder="Select Grievance Categories"
          />
        </Col>
        {isOtherCategorySelected && (
          <Col md={4} sm={6}>
            <InputFieldText
              label={t("Other Category Name") + "*"}
              name="complainDetails.otherCategory"
              formik={formik}
              placeholder="Enter other category name"
            />
          </Col>
        )}
        <Col md={4} sm={6}>
          <InputFieldText
            label={
              t(
                "Name of the employee / employees (specify employee ID or department) or department against whom grievance has been filed (if any)"
              ) + "*"
            }
            name="complainDetails.employeeName"
            formik={formik}
            placeholder="Enter employee/department name"
          />
        </Col>
        <Row>
          <InputFieldText
            label={
              t(
                "Details of the complaint, facts giving rise to the grievance"
              ) + "*"
            }
            name="complainDetails.complaintDetails"
            formik={formik}
            placeholder="Enter complaint details"
            multiline
            rows={4}
          />
        </Row>
        <Row>
          <InputFieldText
            label={t("Nature of relief sought from the Forum") + "*"}
            name="complainDetails.reliefNature"
            formik={formik}
            placeholder="Enter reflief sought"
            multiline
            rows={4}
          />
        </Row>
        <Row>
          <Typography>
            {t(
              "List of documents enclosed (Please enclose copies of any relevant documents which support the facts giving rise to the grievance)"
            )}
            {<span className="text-danger">*</span>}
          </Typography>
        </Row>
        <Row>
          <div className="d-flex gap-3 flex-wrap">
            {!!documents.length &&
              documents.map(({ documentUrl, documentName, file }, index) => (
                <div className="image-container mt-4" key={index}>
                  <a rel="noreferrer">
                    <Box
                      component="img"
                      className="image d-block m-auto"
                      alt="document image"
                      src={
                        getFileExtension(documentName).toLowerCase() === "pdf"
                          ? pdfImg
                          : file
                          ? documentUrl
                          : getImageUrl(documentUrl)
                      }
                    />
                  </a>
                  <i
                    className="ri-close-circle-line fs-3 close-btn"
                    onClick={() => {
                      setFieldValue(
                        "complainDetails.documents",
                        documents.filter((_, ind) => ind !== index)
                      );
                    }}
                  />
                  <Tooltip title={documentName}>
                    <DocumentName fontSize={13} variant="body2">
                      {documentName}
                    </DocumentName>
                  </Tooltip>
                </div>
              ))}

            <Box
              component="label"
              className="mb-3 p-1 cursor-pointer"
              sx={{
                border: "1px dashed black",
              }}
            >
              <UploadImage>
                <AddIcon />
              </UploadImage>
              <input
                ref={ref}
                type="file"
                accept="image pdf/*"
                onChange={fileChangeHandler}
                multiple
                hidden
              />
            </Box>
            {isError && (
              <Box>
                <Typography
                  variant="subtitle2"
                  sx={{ color: theme.palette.error.main, pt: 1.2 }}
                >
                  {t(errorMessage)}
                </Typography>
              </Box>
            )}
          </div>
        </Row>
      </Row>
    </>
  );
};

ComplainDetails.propTypes = {
  formik: PropTypes.object,
  isDisabled: PropTypes.bool,
  t: PropTypes.func,
};
export default withTranslation()(ComplainDetails);
